import { useQuery } from '@tanstack/react-query'
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { startingTab, ticketQuery, type TicketCard, type TicketTab } from '../api/easeedesk'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { TicketAppLabel } from '../applications/tickets/TicketAppLabel'
import { TicketActions } from './TicketActions'
import { TicketAffectedItems } from './TicketAffectedItems'
import { TicketFamily, TicketProblems } from './TicketBands'
import { TicketChangePanel } from './TicketChangePanel'
import { TicketChanges } from './TicketChanges'
import { TicketCommentForm } from './TicketCommentForm'
import { TicketContacts } from './TicketContacts'
import { TicketContract } from './TicketContract'
import { TicketDeviceTab } from './TicketDeviceTab'
import { TicketXdrTab } from './TicketXdrTab'
import { TicketVendorEscalation } from './TicketVendorEscalation'
import { TicketAppIncident } from './TicketAppIncident'
import { TicketNis2Art23 } from './TicketNis2Art23'
import { TicketDispatch } from './TicketDispatch'
import { TicketHeaderTable } from './TicketHeaderTable'
import { TicketLogs } from './TicketLogs'
import { TicketChat, TicketConversation, TicketSms } from './TicketMessages'
import { TicketManuals } from './TicketManuals'
import { TicketPassSafe } from './TicketPassSafe'
import { TicketPriorityMatrix } from './TicketPriorityMatrix'
import { TicketRelated } from './TicketRelated'
import { TicketRemoteConnect, TicketRemoteControl } from './TicketRemote'
import { TicketSolutions } from './TicketSolutions'
import { TicketStandards } from './TicketStandards'
import { TicketSubordinates } from './TicketSubordinates'
import { TicketSummary } from './TicketSummary'
import { TabCount, useTicketTabCounts } from './TicketTabCounts'
import { NEW_TAB, NewTabScope } from '../ui/NewTab'

/**
 * Fisa unui tichet Easeedesk - `Easeedesk::ticket` cu `ticket/index.php`,
 * `ticket/details.php` si `tickets/details/index.php`.
 *
 * Aceeasi asezare: randul de sus cu cele 17 coloane, sub el banda de rudenie si
 * banda problemei, iar dedesubt doua coloane egale - filele de detalii in
 * stanga, cele de actiuni in dreapta - fiecare cu panoul ei.
 *
 * O SINGURA CERERE pentru carcasa. In portal erau patru, una dupa alta: pagina
 * desena un `<div class="ticketTable">` gol, care cerea `ticketTable`, care
 * desena un tabel gol, care cerea `ticketData` si abia apoi `expandTicket`.
 *
 * Filele care nu s-au mutat inca isi pastreaza locul si spun unde se deschid.
 * O fila care ar disparea din rand ar face ecranul nou sa arate ALTFEL decat cel
 * vechi, iar cine o cauta ar crede ca a pierdut-o.
 */

/**
 * Filele care au ecran nou, pe cheia lor din portal.
 *
 * UN TICHET DIN ARHIVA are alta cheie pentru aceeasi fila
 * (`easeedesk_closed_tickets/...`), fiindca in portal o servea alt controller -
 * unul care cauta in tabela in care tichetul chiar se afla. Aici cele doua chei
 * duc, aproape peste tot, la ACELASI ecran: capul din Symfony e adresat numai
 * cu numarul tichetului, iar `DbalTicketSource::find()` cauta si in arhiva. Deci
 * nu e nimic de scris a doua oara - doar de recunoscut si a doua adresa.
 *
 * Cine deosebeste tichetul terminat de cel viu e ecranul insusi, acolo unde
 * chiar conteaza: formularul de comentariu isi cere starea de la server si nu
 * deseneaza casuta pe un tichet care nu mai primeste comentarii.
 */
const SCREENS: Record<string, (props: { id: number }) => ReactNode> = {
  'easeedesk/ticketLogs/': ({ id }) => <TicketLogs id={id} />,
  'easeedesk_closed_tickets/ticketLogs/': ({ id }) => <TicketLogs id={id} />,
  /* Rezumatul intreg, nu doar masinile: `TicketSummary` deseneaza cele doua
     coloane si cere el panoul de echipamente pentru cea din dreapta. */
  'easeedesk/ticketSummary/': ({ id }) => <TicketSummary id={id} />,
  'easeedesk_closed_tickets/ticketSummary/': ({ id }) => <TicketSummary id={id} />,
  'easeedesk/ticketSubordinates/': ({ id }) => <TicketSubordinates id={id} />,
  'easeedesk/ticketComments/': ({ id }) => <TicketCommentForm id={id} panel />,
  'easeedesk_closed_tickets/ticketComments/': ({ id }) => <TicketCommentForm id={id} panel />,
  'easeedesk/ticketChanges/': ({ id }) => <TicketChanges id={id} />,
  'easeedesk_closed_tickets/ticketChanges/': ({ id }) => <TicketChanges id={id} />,
  'easeedesk/ticketHistory/': ({ id }) => <TicketRelated id={id} />,
  'easeedesk_closed_tickets/ticketHistory/': ({ id }) => <TicketRelated id={id} />,
  'easeedesk/ticketAffectedItems/': ({ id }) => <TicketAffectedItems id={id} />,
  'easeedesk/contacts/': ({ id }) => <TicketContacts id={id} />,
  'easeedesk_closed_tickets/contacts/': ({ id }) => <TicketContacts id={id} />,
  'easeedesk/tab/hardware/': ({ id }) => <TicketDeviceTab id={id} tab="hardware" />,
  'easeedesk_closed_tickets/tab/hardware/': ({ id }) => <TicketDeviceTab id={id} tab="hardware" />,
  /* Incidentul XDR al tichetului (docs XDR 05, PR 2). Aceeasi adresa si in
     arhiva: incidentul sta in tabelele Securitee, nu in ale tichetului. */
  'easeedesk/tab/xdr/': ({ id }) => <TicketXdrTab id={id} />,
  'easeedesk/tab/live/': ({ id }) => <TicketDeviceTab id={id} tab="live" />,
  'easeedesk_closed_tickets/tab/live/': ({ id }) => <TicketDeviceTab id={id} tab="live" />,
  'easeedesk/tab/health/': ({ id }) => <TicketDeviceTab id={id} tab="health" />,
  'easeedesk_closed_tickets/tab/health/': ({ id }) => <TicketDeviceTab id={id} tab="health" />,
  'easeedesk/tab/software/': ({ id }) => <TicketDeviceTab id={id} tab="software" />,
  'easeedesk_closed_tickets/tab/software/': ({ id }) => <TicketDeviceTab id={id} tab="software" />,
  'easeedesk/ticketAllActions/': ({ id }) => <TicketActions id={id} />,
  'easeedesk/ticketConversation/': ({ id }) => <TicketConversation id={id} />,
  'easeedesk/chatConversation/': ({ id }) => <TicketChat id={id} />,
  'easeedesk_closed_tickets/chatConversation/': ({ id }) => <TicketChat id={id} />,
  'easeedesk/smsConversation/': ({ id }) => <TicketSms id={id} />,
  'easeedesk/remoteConnect/': ({ id }) => <TicketRemoteConnect id={id} />,
  'easeedesk/remoteControl/': ({ id }) => <TicketRemoteControl id={id} />,
  'easeedesk/imageAnalizer/': ({ id }) => <TicketManuals id={id} />,
  'easeedesk_closed_tickets/imageAnalizer/': ({ id }) => <TicketManuals id={id} />,
  'easeedesk/itPolicies/': ({ id }) => <TicketStandards id={id} />,
  'easeedesk_closed_tickets/itPolicies/': ({ id }) => <TicketStandards id={id} />,
  'easeedesk/passSafe/': ({ id }) => <TicketPassSafe id={id} />,
  'easeedesk_closed_tickets/passSafe/': ({ id }) => <TicketPassSafe id={id} />,
  'easeedesk/ticketContract/': ({ id }) => <TicketContract id={id} />,
  'easeedesk_closed_tickets/ticketContract/': ({ id }) => <TicketContract id={id} />,
  'easeedesk/ticketSolutions/': ({ id }) => <TicketSolutions id={id} />,
  'easeedesk/fastDispatch/': ({ id }) => <TicketDispatch id={id} />,
}

export function TicketPage({ id, tab }: { id: string | undefined; tab?: string }) {
  const ticketId = Number(id)

  /* "Ticket is undefined" - acelasi mesaj, aceeasi cutie ca in `ticket/index.php`. */
  if (!id || !Number.isFinite(ticketId) || ticketId < 1) {
    return (
      <Content>
        <div className="notifications">
          <div className="alert alert-danger">
            <i className="fas fa-info-circle"></i> Ticket is undefined
          </div>
        </div>
      </Content>
    )
  }

  return (
    <Content>
      <div id="content">
        <TicketCard id={ticketId} tab={tab} />
      </div>
    </Content>
  )
}

/**
 * Fisa asa cum o deseneaza PAGINA tichetului: randul de sus, si sub el tot ce
 * intoarcea `expandTicket`.
 *
 * `ticket/index.php` -> `ticketTable` -> `ticket/details.php` desena tabelul de
 * 17 coloane cu randul tichetului in el, iar `TicketTable.expandTable()` lipea
 * dedesubt, tot in acelasi tabel, raspunsul lui `expandTicket`. Cele doua bucati
 * sunt aici una langa alta.
 */
export function TicketCard({ id, tab }: { id: number; tab?: string }) {
  const { data, error } = useQuery(ticketQuery(id))

  if (error) {
    /* Mesajul paginii, din `ticket/index.php`: alta cutie si alte cuvinte decat
       cele ale randului din lista - vezi `TicketExpandedRow`. */
    return (
      <div className="note note-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> Sorry but there was an error
      </div>
    )
  }
  if (!data) {
    return <Spinner />
  }

  return (
    <div className="ticketTable">
      {data.row && <TicketHeaderTable row={data.row} />}
      <TicketAppLabel id={id} />
      <CardBody card={data} tab={tab} />
    </div>
  )
}

/**
 * Randul desfasurat de sub un tichet din LISTA - perechea lui
 * `Easeedesk::expandTicket()`.
 *
 * NU e aceeasi bucata ca fisa de pe pagina, si portalul nu le-a amestecat
 * niciodata: `expandTicket` intorcea NUMAI `tickets/details/index.php` - banda
 * de rudenie, banda problemei si cele doua coloane de file - fiindca randul
 * tichetului e chiar randul listei de deasupra, cel pe care s-a apasat `+`.
 *
 * Aici se desena `TicketCard` intreg, deci sub randul listei cadea inca un cap
 * de tabel, cu ALTE coloane decat cele ale listei (17 in loc de 22) si cu alte
 * latimi, si acelasi tichet scris a doua oara. Masurat in Edge pe foile
 * adevarate: 90px de tabel in plus, care impingeau banda de rudenie de la 1px
 * la 103px sub marginea de sus a celulei, si faceau randul desfasurat 365px in
 * loc de 263px.
 */
export function TicketExpandedRow({ id }: { id: number }) {
  const { data, error } = useQuery(ticketQuery(id))

  if (error) {
    /* Cutia si cuvintele lui `ExpandTicket.showError()` din
       `public/js/easeedesk/easeedesk.js`, care sunt ALTELE decat ale paginii.
       Aceeasi deosebire ca in portal; n-o facem noi. */
    return (
      <div className="panel-body">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle"></i> Error! Something went wrong.
        </div>
      </div>
    )
  }
  if (!data) {
    return <Spinner />
  }

  /* Tot ce duce in alta parte din randul desfasurat se deschide in fila noua:
     tichetul din lista ramane deschis. Vezi `ui/NewTab.tsx`. */
  return (
    <NewTabScope>
      <TicketAppLabel id={id} />
      <CardBody card={data} />
    </NewTabScope>
  )
}

/** Ce intorcea `expandTicket`: cele doua benzi si cele doua coloane de file. */
function CardBody({ card, tab }: { card: TicketCard; tab?: string }) {
  return (
    <>
      <TicketFamily
        parent={card.family.parent}
        kids={card.family.children}
        causedBy={card.family.causedBy}
        caused={card.family.caused}
      />
      <TicketProblems problems={card.problems} />
      {/* Aplicatia de business a aparatului: escaladarea la furnizor si termenele ei. */}
      {/* Incidentul aplicatiei: "Part of incident: <aplicatia> unavailable". */}
      <TicketAppIncident id={card.id} />
      {/* Art. 23 NIS2: incident semnificativ, termenele 24 h / 72 h / o luna pentru PNRISC. */}
      <TicketNis2Art23 id={card.id} />
      <TicketVendorEscalation id={card.id} />
      {/* Urgenta x impact (CEC L25) - nu se deseneaza cand matricea e stinsa. */}
      <TicketPriorityMatrix id={card.id} />
      {/* Cererea de schimbare: nu se deseneaza deloc pe un tichet care nu e change. */}
      <TicketChangePanel id={card.id} />
      <Panels card={card} tab={tab} />
    </>
  )
}

function Panels({ card, tab }: { card: TicketCard; tab?: string }) {
  const [details, setDetails] = useState(() => startingTab(tab, card.tabs.details))
  /*
   * Si coloana din dreapta porneste cu prima fila deschisa.
   *
   * `TicketTabs()` din `public/js/easeedesk/easeedesk.js` face
   * `$('ul.tickets-tabs-' + ticket).each(...).eq(0).trigger('click')` - adica
   * pe AMANDOUA randurile de file, nu doar pe cel de detalii. Aici pornea de la
   * sirul gol, deci jumatatea dreapta a fisei se deschidea alba.
   */
  const [action, setAction] = useState(() => startingTab(undefined, card.tabs.actions))
  /* Numerele filelor din stanga - vezi `TicketTabCounts.tsx`. */
  const counts = useTicketTabCounts(card.id)

  return (
    <div className={card.xdr ? 'row ticket-xdr' : 'row'} style={{ margin: 0 }}>
      <div className="col-md-6 ticket-details" style={{ padding: 0, margin: 0, backgroundColor: '#FFF', borderRight: '1px solid #ddd', marginRight: -1 }}>
        <TabStrip ticket={card.id} tabs={card.tabs.details} current={details} badges={card.badges} counts={counts} onOpen={setDetails} />
        <div className="tab-content" id={`details-aria-content-${card.id}`} style={{ margin: 0, padding: 20 }}>
          <Screen id={card.id} href={details} />
        </div>
      </div>

      <div className="col-md-6 ticket-actions" style={{ padding: 0, margin: 0, borderLeft: '1px solid #ddd', backgroundColor: '#FFF' }}>
        <TabStrip ticket={card.id} tabs={card.tabs.actions} current={action} badges={card.badges} onOpen={setAction} />
        <div className="tab-content" id={`actions-aria-content-${card.id}`} style={{ margin: 0, padding: 20 }}>
          {action !== '' && <Screen id={card.id} href={action} />}
        </div>
      </div>
    </div>
  )
}

/**
 * Randul de file, cu hamburgerul din capatul lui.
 *
 * Filele care nu incap pe un rand nu se rup pe al doilea: intra intr-un meniu
 * `≡▾` lipit de marginea din dreapta - `bootstrap-tabdrop`, pluginul pe care
 * portalul il pune pe fiecare `ul.tickets-tabs`. Un tichet poate avea
 * cincisprezece file, iar coloana e jumatate de ecran; fara el, randul se face
 * trei randuri si impinge tot ce urmeaza in jos.
 *
 * MASURATOAREA se face o data, cu toate filele desenate, si se tine: latimile
 * nu se schimba cand se ascunde una. Pluginul vechi facea altfel - se uita la
 * `offsetTop` si recurgea - si de-aia il si prindea uneori latimea GRESITA:
 * randul extins din lista isi masura filele inainte ca celula sa fi ajuns la
 * latimea ei, si atunci ascundea file care aveau loc berechet. In fotografia de
 * pe cec-master se vad patru file si un hamburger intr-o coloana de 940px.
 */
function TabStrip({
  ticket,
  tabs,
  current,
  badges,
  counts,
  onOpen,
}: {
  ticket: number
  tabs: TicketTab[]
  current: string
  badges: Record<string, number>
  /** Numerele filelor, pe `id`-ul lor; numai randul din stanga le are. */
  counts?: Record<string, number>
  onOpen(href: string): void
}) {
  const strip = useRef<HTMLUListElement>(null)
  /** Latimile masurate o data, cu tot randul desenat: `[file..., hamburger]`. */
  const sizes = useRef<{ tabs: number[]; drop: number } | null>(null)
  /** `MEASURING` = prima desenare, cu toate filele SI hamburgerul pe ecran. */
  const [fit, setFit] = useState(MEASURING)

  /*
   * Numerele vin DUPA ce filele sunt pe ecran (alta cerere), si o insigna
   * lateste fila. Latimile masurate fara ea ar baga in meniul `≡` mai putine
   * file decat trebuie, iar randul s-ar rupe pe doua. Cand se schimba un numar
   * se masoara din nou, tot inaintea vopsirii.
   */
  const shownCounts = tabs.map((t) => counts?.[t.id] ?? 0).join(',')
  const measuredCounts = useRef(shownCounts)
  useLayoutEffect(() => {
    if (measuredCounts.current === shownCounts) return
    measuredCounts.current = shownCounts
    sizes.current = null
    setFit(MEASURING)
  }, [shownCounts])
  const measuring = fit === MEASURING

  useLayoutEffect(() => {
    const ul = strip.current
    if (!ul) return

    const relayout = (): void => {
      if (sizes.current === null) {
        sizes.current = {
          tabs: [...ul.querySelectorAll<HTMLLIElement>('li.tk-tab')].map((li) => li.offsetWidth),
          drop: (ul.querySelector<HTMLLIElement>('li.tabdrop')?.offsetWidth ?? 0) || 1,
        }
      }

      const all = sizes.current.tabs.reduce((sum, w) => sum + w, 0)

      /* Incap toate: hamburgerul nu mai are ce tine si nu-si mai cere locul. */
      if (all <= ul.clientWidth) {
        setFit(tabs.length)

        return
      }

      const room = ul.clientWidth - sizes.current.drop
      let used = 0
      let count = 0
      for (const width of sizes.current.tabs) {
        if (used + width > room) break
        used += width
        count++
      }

      setFit(count)
    }

    relayout()

    /* Fara `ResizeObserver` (jsdom, browsere vechi) randul se aseaza o data si
       ramane asa - nu se strica nimic, doar nu se remasoara la redimensionare. */
    if (typeof ResizeObserver === 'undefined') return

    const watch = new ResizeObserver(relayout)
    watch.observe(ul)

    return () => watch.disconnect()
    /* `measuring`: dupa o remasurare ceruta de un numar nou, randul intreg e
       iar pe ecran si trebuie citit. */
  }, [tabs, measuring])

  if (tabs.length === 0) return null

  const shown = measuring ? tabs : tabs.slice(0, fit)
  const hidden = measuring ? [] : tabs.slice(fit)

  const item = (tab: TicketTab, inMenu: boolean) => (
    <li
      key={tab.href + tab.label}
      role="presentation"
      className={`${inMenu ? '' : 'tk-tab '}${current === tab.href ? 'active' : ''}`.trim() || undefined}
    >
      <a
        style={tab.color ? { color: tab.color } : undefined}
        href={`/app/easeedesk/ticket/${ticket}${tab.id ? `/${tab.id}` : ''}`}
        id={tab.id || undefined}
        role="tab"
        onClick={(ev) => {
          ev.preventDefault()
          onOpen(tab.href)
        }}
      >
        {/* Bulina care clipeste inaintea lui "Live", ca in macheta veche. */}
        {tab.live && <span className="dots dots-danger faa-flash animated align-middle"></span>}
        {tab.live ? <strong> {tab.label}</strong> : tab.label}
        {tab.badge && (
          <span className="badge" style={{ padding: '3px 5px', verticalAlign: 'top', fontSize: 10, backgroundColor: tab.badge.color }} id={tab.badge.id}>
            {badges[tab.badge.count] ?? 0}
          </span>
        )}
        {counts && <TabCount tab={tab.id} count={counts[tab.id]} />}
        {/*
         * Locul insignei scumpe, gol. In portal se umple dintr-o cerere
         * separata dupa ce filele sunt pe ecran (`ticketDynamicBadges`);
         * aceea nu s-a mutat inca, si locul ramane gol - adica exact starea
         * in care se deseneaza si acolo.
         */}
        <span className="tk-dyn-badge" data-dyn-badge={tab.id}></span>
      </a>
    </li>
  )

  return (
    <ul ref={strip} className={`nav nav-tabs tickets-tabs metro tickets-tabs-${ticket}`} role="tablist">
      {(measuring || hidden.length > 0) && (
        <TabDrop hasActive={hidden.some((t) => t.href === current)}>{hidden.map((tab) => item(tab, true))}</TabDrop>
      )}
      {shown.map((tab) => item(tab, false))}
    </ul>
  )
}

/**
 * Prima desenare, cea in care se masoara: toate filele SI hamburgerul sunt pe
 * ecran, ca sa li se poata citi latimile. Tine o singura trecere de asezare,
 * inaintea vopsirii (`useLayoutEffect`), deci nu clipeste nimic.
 *
 * Latimea hamburgerului se MASOARA, nu se scrie: foaia pluginului
 * (`bootstrap-tabdrop/css/tabdrop.css`, cu `.tabdrop { width: 120px }`) nu e
 * incarcata nici in portal - nu e in `header.php` - deci 120 ar fi fost un
 * numar luat de nicaieri.
 */
const MEASURING = -1

/** `<li class="dropdown pull-right tabdrop">` al pluginului, cu meniul lui. */
function TabDrop({ hasActive, children }: { hasActive: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false)

  return (
    <li className={`dropdown pull-right tabdrop${open ? ' open' : ''}${hasActive ? ' active' : ''}`}>
      <a
        className="dropdown-toggle"
        href="#"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(ev) => {
          ev.preventDefault()
          setOpen(!open)
        }}
      >
        <span className="display-tab">
          <i className="fas fa-bars"></i>
        </span>
        <b className="caret"></b>
      </a>
      <ul className="dropdown-menu" onClick={() => setOpen(false)}>
        {children}
      </ul>
    </li>
  )
}

/**
 * Panoul unei file. Cele care nu s-au mutat inca trimit omul acolo unde chiar se
 * deschid, in loc sa lase un dreptunghi alb care nu spune nimic.
 */
function Screen({ id, href }: { id: number; href: string }) {
  const known = SCREENS[href]
  if (known) return known({ id })

  return (
    <div className="alert alert-info" role="alert">
      <i className="fas fa-info-circle" aria-hidden="true"></i> This tab still opens in the current portal:{' '}
      <a href={`/${href}${id}`} {...NEW_TAB}>/{href}
        {id}
      </a>
    </div>
  )
}
